import React, { useState, useEffect } from 'react';
import { XMarkIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { riskApi } from '@/services/api';

interface RiskSettingsProps {
  riskProfile: any;
  onClose: () => void;
  onSave: () => void;
}

const RiskSettings: React.FC<RiskSettingsProps> = ({
  riskProfile,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState({
    profileName: '',
    maxPositionSizePct: 25,
    maxDailyVolume: 10000,
    maxTotalExposurePct: 80,
    maxSingleAssetPct: 30,
    globalStopLossPct: 20,
    dailyLossLimitPct: 5,
    maxDrawdownPct: 15,
    maxVarDailyPct: 3,
    minSharpeRatio: 0.5,
    correlationLimit: 0.8,
    maxTradesPerDay: 50,
    cooldownPeriodMinutes: 60,
    requireConfirmationAbove: 5000,
    autoEmergencyStop: true,
    emergencyStopLossPct: 10
  });

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (riskProfile) {
      setFormData({
        profileName: riskProfile.profileName || '',
        maxPositionSizePct: riskProfile.maxPositionSizePct || 25,
        maxDailyVolume: riskProfile.maxDailyVolume || 10000,
        maxTotalExposurePct: riskProfile.maxTotalExposurePct || 80,
        maxSingleAssetPct: riskProfile.maxSingleAssetPct || 30,
        globalStopLossPct: riskProfile.globalStopLossPct || 20,
        dailyLossLimitPct: riskProfile.dailyLossLimitPct || 5,
        maxDrawdownPct: riskProfile.maxDrawdownPct || 15,
        maxVarDailyPct: riskProfile.maxVarDailyPct || 3,
        minSharpeRatio: riskProfile.minSharpeRatio || 0.5,
        correlationLimit: riskProfile.correlationLimit || 0.8,
        maxTradesPerDay: riskProfile.maxTradesPerDay || 50,
        cooldownPeriodMinutes: riskProfile.cooldownPeriodMinutes || 60,
        requireConfirmationAbove: riskProfile.requireConfirmationAbove || 5000,
        autoEmergencyStop: riskProfile.autoEmergencyStop !== false,
        emergencyStopLossPct: riskProfile.emergencyStopLossPct || 10
      });
    }
  }, [riskProfile]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setIsDirty(true);
    setError(null);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const updateData = {
        ...formData,
        id: riskProfile?.id
      };

      await riskApi.updateRiskProfile(updateData);
      
      setIsDirty(false);
      onSave();
      onClose();

    } catch (err: any) {
      console.error('Failed to save risk settings:', err);
      setError(err.message || 'Failed to save risk settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isDirty) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-0 border max-w-4xl shadow-lg rounded-lg bg-white">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Cog6ToothIcon className="h-6 w-6 text-indigo-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">Risk Management Settings</h3>
              <p className="text-sm text-gray-500">Configure your risk limits and trading parameters</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Form Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* General Settings */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 border-b pb-2">General Settings</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Name
                </label>
                <input
                  type="text"
                  value={formData.profileName}
                  onChange={(e) => handleInputChange('profileName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="My Risk Profile"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Position Size: {formData.maxPositionSizePct}%
                </label>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={formData.maxPositionSizePct}
                  onChange={(e) => handleInputChange('maxPositionSizePct', Number(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Conservative (1%)</span>
                  <span>Aggressive (50%)</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Daily Volume
                </label>
                <input
                  type="number"
                  min="100"
                  max="100000"
                  step="500"
                  value={formData.maxDailyVolume}
                  onChange={(e) => handleInputChange('maxDailyVolume', Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum daily trading volume in USD</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Portfolio Exposure: {formData.maxTotalExposurePct}%
                </label>
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={formData.maxTotalExposurePct}
                  onChange={(e) => handleInputChange('maxTotalExposurePct', Number(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Conservative (10%)</span>
                  <span>Full Investment (100%)</span>
                </div>
              </div>
            </div>

            {/* Risk Controls */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 border-b pb-2">Risk Controls</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Global Stop Loss: {formData.globalStopLossPct}%
                </label>
                <input
                  type="range"
                  min="5"
                  max="50"
                  value={formData.globalStopLossPct}
                  onChange={(e) => handleInputChange('globalStopLossPct', Number(e.target.value))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Portfolio-wide stop loss trigger</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Daily Loss Limit: {formData.dailyLossLimitPct}%
                </label>
                <input
                  type="range"
                  min="1"
                  max="20"
                  value={formData.dailyLossLimitPct}
                  onChange={(e) => handleInputChange('dailyLossLimitPct', Number(e.target.value))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum daily loss before alert</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Emergency Stop Loss: {formData.emergencyStopLossPct}%
                </label>
                <input
                  type="range"
                  min="3"
                  max="30"
                  value={formData.emergencyStopLossPct}
                  onChange={(e) => handleInputChange('emergencyStopLossPct', Number(e.target.value))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Automatic emergency stop trigger</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Single Asset: {formData.maxSingleAssetPct}%
                </label>
                <input
                  type="range"
                  min="5"
                  max="100"
                  value={formData.maxSingleAssetPct}
                  onChange={(e) => handleInputChange('maxSingleAssetPct', Number(e.target.value))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum concentration in single asset</p>
              </div>
            </div>

            {/* Trading Limits */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 border-b pb-2">Trading Limits</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Trades Per Day
                </label>
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={formData.maxTradesPerDay}
                  onChange={(e) => handleInputChange('maxTradesPerDay', Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cooldown Period (Minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  max="1440"
                  value={formData.cooldownPeriodMinutes}
                  onChange={(e) => handleInputChange('cooldownPeriodMinutes', Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">Wait time after loss before next trade</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Require Confirmation Above
                </label>
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={formData.requireConfirmationAbove}
                  onChange={(e) => handleInputChange('requireConfirmationAbove', Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">Manual confirmation required for trades above this amount (USD)</p>
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 border-b pb-2">Advanced Settings</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Drawdown: {formData.maxDrawdownPct}%
                </label>
                <input
                  type="range"
                  min="5"
                  max="50"
                  value={formData.maxDrawdownPct}
                  onChange={(e) => handleInputChange('maxDrawdownPct', Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Daily VaR Limit: {formData.maxVarDailyPct}%
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="10"
                  step="0.1"
                  value={formData.maxVarDailyPct}
                  onChange={(e) => handleInputChange('maxVarDailyPct', Number(e.target.value))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Value at Risk daily limit</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Min Sharpe Ratio: {formData.minSharpeRatio}
                </label>
                <input
                  type="range"
                  min="0"
                  max="3"
                  step="0.1"
                  value={formData.minSharpeRatio}
                  onChange={(e) => handleInputChange('minSharpeRatio', Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.autoEmergencyStop}
                    onChange={(e) => handleInputChange('autoEmergencyStop', e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Enable Automatic Emergency Stops
                  </span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Automatically trigger emergency stop on critical violations
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            {isDirty && (
              <span className="text-orange-600">• Unsaved changes</span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleClose}
              disabled={isSaving}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !isDirty}
              className="px-4 py-2 bg-indigo-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskSettings; 